/*
Copyright (c) 2012-2025 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { getApiUrl } from "constants/domain"
import { IncidentEndpoint } from "constants/endpoints"
import { createReducer } from "store/create-reducer"
import { AxiosAction } from "types/api"
import {
  defaultPageSizeIncidents,
  appeaseStatusKey,
  appealStatusKey,
} from "pages/feedback/consts"
import {
  IncidentListState,
  IncidentState,
  IncidentStat,
} from "./incidents.type"

const NAMESPACE = "INCIDENTS"

const apiUrl = getApiUrl()

export const actions = {
  FETCH_LIST: `${NAMESPACE}/FETCH_LIST`,
  FETCH_LIST_SUCCESS: `${NAMESPACE}/FETCH_LIST_SUCCESS`,
  FETCH_LIST_FAIL: `${NAMESPACE}/FETCH_LIST_FAIL`,
  FETCH_INCIDENT_STAT: `${NAMESPACE}/FETCH_INCIDENT_STAT`,
  FETCH_INCIDENT_STAT_SUCCESS: `${NAMESPACE}/FETCH_INCIDENT_STAT_SUCCESS`,
  FETCH_INCIDENT_STAT_FAIL: `${NAMESPACE}/FETCH_INCIDENT_STAT_FAIL`,
}

type ActionTypes =
  | typeof actions.FETCH_LIST
  | typeof actions.FETCH_LIST_SUCCESS
  | typeof actions.FETCH_LIST_FAIL

interface IncidentListParams {
  merchantID: string
  incidentType?: string[]
  resolutionType?: string[]
  fromTime?: string
  endTime?: string
  page?: number
  pageSize?: number
}

const fetchIncidentList = ({
  merchantID,
  incidentType = [],
  resolutionType = [],
  fromTime = "",
  endTime = "",
  page = 1,
  pageSize = defaultPageSizeIncidents,
}: IncidentListParams) => {
  const queryParams = new URLSearchParams({
    incidentSevereGroupings: JSON.stringify(incidentType),
    fromTime,
    endTime,
    page: page.toString(),
  })

  // Manually add appeaseStatuses because its value is number
  if (resolutionType.length > 0) {
    const appeaseStatuses = resolutionType.filter(val =>
      appeaseStatusKey.includes(
        (Number(val) as unknown) as typeof appeaseStatusKey[number],
      ),
    )
    const appealStatuses = resolutionType.filter(val =>
      appealStatusKey.includes(
        (Number(val) as unknown) as typeof appealStatusKey[number],
      ),
    )

    queryParams.append("appeaseStatuses", `[${appeaseStatuses}]`)
    queryParams.append("appealStatuses", `[${appealStatuses}]`)
  }

  return {
    type: actions.FETCH_LIST,
    payload: {
      request: {
        url: `${apiUrl}/food/merchant${IncidentEndpoint.fetchIncidentsV1}?${queryParams}`,
        method: "get",
        headers: {
          merchantID,
        },
      },
      pagination: {
        page,
        pageSize,
      },
    },
    showError: true,
  }
}

const fetchIncidentStat = (merchantID: string) => {
  return {
    type: actions.FETCH_INCIDENT_STAT,
    payload: {
      request: {
        url: `${apiUrl}/food/merchant${IncidentEndpoint.fetchIncidentStat}?filterBy=SEVEREGROUPING`,
        method: "get",
        headers: {
          merchantID,
        },
      },
    },
    showError: true,
  }
}

export const actionCreator = {
  fetchIncidentList,
  fetchIncidentStat,
}

type ActionCreatorTypes = {
  [K in keyof typeof actionCreator]: ReturnType<typeof actionCreator[K]>
}
export type ActionCreatorTypeList = ActionCreatorTypes[keyof ActionCreatorTypes]

export const initialState = (): IncidentState => ({
  incidents: [] as IncidentListState[],
  hasMore: false,
  isLoading: false,
  total: 0,
})

export const reducer = createReducer<
  IncidentState,
  ActionTypes,
  ActionCreatorTypeList
>(initialState(), {
  [actions.FETCH_LIST]: (state: IncidentState) => ({
    ...state,
    isLoading: true,
  }),
  [actions.FETCH_LIST_SUCCESS]: (
    state: IncidentState,
    action: AxiosAction<IncidentState, ActionCreatorTypes["fetchIncidentList"]>,
  ) => {
    const previousPage =
      action.meta.previousAction?.payload?.pagination?.page || 1
    const pageSize =
      action.meta.previousAction?.payload?.pagination?.pageSize ||
      defaultPageSizeIncidents
    const hasMore = !!action?.payload?.data?.hasMore
    const incidents = action?.payload?.data?.incidents || []

    return {
      ...state,
      isLoading: false,
      incidents,
      hasMore,
      total: hasMore
        ? Number(previousPage) * pageSize + 1
        : Number(previousPage) * pageSize,
    }
  },
  [actions.FETCH_LIST_FAIL]: (state: IncidentState) => ({
    ...state,
    incidents: [], // Reset to empty array when fetch fails
    isLoading: false,
  }),
  [actions.FETCH_INCIDENT_STAT]: (state: IncidentState) => ({
    ...state,
    isLoading: true,
  }),
  [actions.FETCH_INCIDENT_STAT_SUCCESS]: (
    state: IncidentState,
    action: AxiosAction<IncidentStat, ActionCreatorTypes["fetchIncidentStat"]>,
  ) => ({
    ...state,
    isLoading: false,
    incidentStat: action?.payload?.data,
  }),
  [actions.FETCH_INCIDENT_STAT_FAIL]: (state: IncidentState) => ({
    ...state,
    isLoading: false,
    incidentStat: {} as IncidentStat,
  }),
})
